import { BannerConfig, ClusterStat, ContactSettings, ContactSubmission, ContactSubmissionPayload, ResourceAdd, Resource } from "../common/types";
import { ActivityLogEntry } from "../common/types";
import {
    CookiePreferencesCopy,
    DEFAULT_COOKIE_PREFERENCES_COPY,
} from "../components/privacy/cookiePreferencesCopy";

const baseUrlMap = {
  development: process.env.REACT_APP_DEV_BASE_URL || 'https://api.ekcdev.local:5000/api',
  staging: process.env.REACT_APP_STAGING_BASE_URL || 'https://website-staging.ekcbm3.thissl.com/api',
  production: process.env.REACT_APP_PROD_BASE_URL || 'https://enterprise-kc.com/api'
};

type BaseUrlKey = keyof typeof baseUrlMap;
const runtimeEnv = (process.env.REACT_APP_ENV || process.env.NODE_ENV || 'production').toLowerCase();
const host = typeof window !== 'undefined' ? window.location.hostname : '';
const browserOrigin = typeof window !== 'undefined' ? window.location.origin : '';

let envKey: BaseUrlKey = (['development', 'staging', 'production'].includes(runtimeEnv)
  ? runtimeEnv
  : 'development') as BaseUrlKey;

// If the host clearly looks like staging, prefer staging even when NODE_ENV=production (CRA forces this)
if (host.includes('staging') && envKey === 'production') {
  envKey = 'staging';
}

function normalizeBaseUrl(url: string): string {
    return url.replace(/\/+$/, '');
}

function resolveConfiguredBaseUrl(url: string): string {
    const mode = url.trim().toLowerCase();
    if (mode === 'same-origin') return browserOrigin;
    if (mode === 'same-origin-api') return `${browserOrigin}/api`;
    return url;
}

function isLoopbackHost(hostname: string): boolean {
    const cleanHost = hostname.replace(/^\[|\]$/g, '');
    return cleanHost === 'localhost' || cleanHost === '127.0.0.1' || cleanHost === '::1';
}

const preferSameOrigin = envKey === 'development' && Boolean(browserOrigin) && Boolean(host) && !isLoopbackHost(host);
const baseUrl = normalizeBaseUrl(resolveConfiguredBaseUrl(
    process.env.REACT_APP_BASE_URL ||
    (preferSameOrigin ? `${browserOrigin}/api` : baseUrlMap[envKey]) ||
    baseUrlMap.development
));

function buildCandidateBaseUrls(primaryBaseUrl: string): string[] {
    const sameOriginCandidates = browserOrigin ? [
        normalizeBaseUrl(`${browserOrigin}/api`),
        normalizeBaseUrl(browserOrigin),
    ] : [];
    const sameHostCandidates = host ? [
        normalizeBaseUrl(`${window.location.protocol}//${host}:5001`),
        normalizeBaseUrl(`${window.location.protocol}//${host}:5000`),
        normalizeBaseUrl(`${window.location.protocol}//${host}:8050`),
    ] : [];
    const localCandidates = [
        normalizeBaseUrl('http://127.0.0.1:5001'),
        normalizeBaseUrl('http://localhost:5001'),
        normalizeBaseUrl('http://127.0.0.1:5000'),
        normalizeBaseUrl('http://localhost:5000'),
        normalizeBaseUrl('http://127.0.0.1:8050'),
        normalizeBaseUrl('http://localhost:8050'),
    ];
    const candidates = preferSameOrigin
        ? [primaryBaseUrl, ...sameOriginCandidates, ...sameHostCandidates, ...localCandidates]
        : [primaryBaseUrl, ...(envKey === 'development' ? [...sameHostCandidates, ...localCandidates] : [])];
    return Array.from(new Set(candidates.filter(Boolean)));
}

const candidateBaseUrls = buildCandidateBaseUrls(baseUrl);
const debugApi = process.env.REACT_APP_DEBUG_API === 'true';

if (debugApi) {
    console.log('NODE_ENV:', process.env.NODE_ENV);
    console.log('REACT_APP_ENV:', process.env.REACT_APP_ENV);
    console.log('host:', host);
    console.log('envKey:', envKey);
    console.log('baseUrl:', baseUrl);
    console.log('candidateBaseUrls:', candidateBaseUrls);
}

export type LandingPageCmsHero = {
  title?: string;
  description?: string;
  backgroundImage?: string | null;
};

export type LandingPageCmsCard = {
  title: string;
  count?: number;
  image?: string | null;
};

export type LandingPageCmsFact = {
  fact: string;
  image?: string | null;
};

export type LandingPageCms = {
  overrideDefaultContent?: boolean;
  hero?: LandingPageCmsHero | null;
  cards?: LandingPageCmsCard[];
  facts?: LandingPageCmsFact[];
};

export type TrackingSettings = {
    luckyOrange: {
        enabled: boolean;
        siteId: string;
    };
    cookieBanner: CookiePreferencesCopy;
};

export type CmsDocumentSummary = {
    id: number;
    contentKey: string;
    contentType: 'page' | 'global' | string;
    title: string;
    slug?: string;
    routePath?: string;
    status?: string;
    isSystem?: boolean;
    updatedAt?: string;
};

export type CmsRevisionSummary = {
    id: number;
    environment: string;
    action: string;
    actorId?: number;
    actorRole?: string;
    notes?: string;
    createdAt?: string;
};

export type CmsDocumentDetail = CmsDocumentSummary & {
    draft: any;
    staging?: any;
    production?: any;
    seoTitle?: string;
    seoDescription?: string;
    stagedAt?: string;
    publishedAt?: string;
    revisions?: CmsRevisionSummary[];
};

export type CmsApprovalRequest = {
    id: number;
    documentId?: number;
    target: string;
    status: string;
    requestedBy: number;
    requestedByUsername?: string;
    approvedBy?: number;
    approvedByUsername?: string;
    notes?: string;
    createdAt?: string;
    decidedAt?: string;
};

async function fetchWithFallback(url: string, options?: RequestInit): Promise<Response> {
    const fetchOptions: RequestInit = {
        ...options,
        credentials: 'include',
    };

    let lastNetworkError: unknown = null;
    for (const candidate of candidateBaseUrls) {
        const fullUrl = `${candidate}${url}`;
        if (debugApi) {
            console.log('Making API call to:', fullUrl);
        }
        try {
            const response = await fetch(fullUrl, fetchOptions);
            if (!response.ok) {
                return response.json().then(error => { throw error.error || error; });
            }
            return response;
        } catch (error) {
            lastNetworkError = error;
            if (error instanceof TypeError) {
                continue;
            }
            throw error;
        }
    }
    throw lastNetworkError;
}

const publicCmsRequestCache = new Map<string, Promise<any>>();
const cmsDocumentRequestCache = new Map<string, { expiresAt: number; request: Promise<{ data: CmsDocumentDetail }> }>();
const CMS_DOCUMENT_CACHE_TTL_MS = 15_000;

const cloneFallback = <T,>(value: T): T => JSON.parse(JSON.stringify(value));

async function getLocalApiFallback(url: string, cacheKey: string): Promise<any> {
    const normalizedUrl = url.replace(/\?.*$/, '');
    if (normalizedUrl === '/resources/' || cacheKey === 'resources:list') {
        const data = await import('../data/liveApiFallbacks.json');
        return cloneFallback(data.default.resources);
    }
    if (normalizedUrl === '/globe/points') {
        const data = await import('../data/liveApiFallbacks.json');
        return cloneFallback(data.default.globePoints);
    }
    if (normalizedUrl === '/globe/groups') {
        const data = await import('../data/liveApiFallbacks.json');
        return cloneFallback(data.default.globeGroups);
    }
    if (normalizedUrl === '/globe/types') {
        const data = await import('../data/liveApiFallbacks.json');
        return cloneFallback(data.default.globeTypes);
    }
    if (normalizedUrl === '/globe/clusters') {
        const data = await import('../data/liveApiFallbacks.json');
        return cloneFallback(data.default.globeClusters);
    }

    if (cacheKey.startsWith('page:')) {
        const slug = cacheKey.replace(/^page:/, '');
        const seedModule = await import('../cms/publicContentData');
        const seed = seedModule.default as any;
        const page = slug === 'home' ? seed?.landingPage : seed?.pages?.[slug];
        if (page) return { data: cloneFallback(page) };
    }

    if (normalizedUrl === '/cms/landing-page') {
        const seedModule = await import('../cms/publicContentData');
        return { status: 'success', data: cloneFallback((seedModule.default as any).landingPage || {}) };
    }
    if (normalizedUrl === '/cms/site-globals') {
        const seedModule = await import('../cms/publicContentData');
        const seed = seedModule.default as any;
        return {
            data: {
                nav: cloneFallback(seed.nav || {}),
                footer: cloneFallback(seed.footer || {}),
                banner: cloneFallback(seed.banner || {}),
                tracking: {
                    luckyOrange: { enabled: false, siteId: '' },
                    cookieBanner: cloneFallback(DEFAULT_COOKIE_PREFERENCES_COPY),
                },
            },
        };
    }
    if (normalizedUrl === '/cms/banner') {
        const seedModule = await import('../cms/publicContentData');
        return { status: 'success', data: cloneFallback((seedModule.default as any).banner || {}) };
    }
    if (normalizedUrl === '/cms/constants') {
        const seedModule = await import('../cms/publicContentData');
        return { data: cloneFallback((seedModule.default as any).constants || {}) };
    }
    if (normalizedUrl === '/cms/resources/templates') {
        const seedModule = await import('../cms/publicContentData');
        const templates = (seedModule.default as any).resourceTemplates || {};
        return { data: Object.entries(templates).map(([slug, template]) => ({ slug, ...(template as Record<string, any>) })) };
    }

    throw new Error(`No local fallback for ${url}`);
}

function getCachedPublicJson<T = any>(url: string, cacheKey = url): Promise<T> {
    const cached = publicCmsRequestCache.get(cacheKey);
    if (cached) return cached as Promise<T>;

    const request = fetchWithFallback(url, { method: 'GET', cache: 'no-store' })
        .then((response) => response.json())
        .catch((error) => {
            return getLocalApiFallback(url, cacheKey).catch(() => {
                publicCmsRequestCache.delete(cacheKey);
                throw error;
            });
        });
    publicCmsRequestCache.set(cacheKey, request);
    request.then(
        () => {
            if (publicCmsRequestCache.get(cacheKey) === request) {
                publicCmsRequestCache.delete(cacheKey);
            }
        },
        () => {
            if (publicCmsRequestCache.get(cacheKey) === request) {
                publicCmsRequestCache.delete(cacheKey);
            }
        },
    );
    return request as Promise<T>;
}

function clearPublicCmsCache(cacheKey?: string): void {
    if (cacheKey) {
        publicCmsRequestCache.delete(cacheKey);
        return;
    }
    publicCmsRequestCache.clear();
}

function clearCmsDocumentCache(contentKey?: string): void {
    if (contentKey) {
        cmsDocumentRequestCache.delete(contentKey);
        return;
    }
    cmsDocumentRequestCache.clear();
}

/***********************************************************************************************
                                        CREATE TEST 6
***********************************************************************************************/

export async function addGlobePoint({ data, apiKey }: { data: any; apiKey: string }): Promise<any> {
    const options: RequestInit = {
        method: 'POST',
        headers: {
            'Content-Type': 'application/json',
            'API-Key': apiKey
        },
        body: JSON.stringify(data),
    };
    const response = await fetchWithFallback('/globe/points', options);
    return response.json();
}

export async function addGlobeType({ data, apiKey }: { data: any; apiKey: string }): Promise<any> {
    const options: RequestInit = {
        method: 'POST',
        headers: {
            'Content-Type': 'application/json',
            'API-Key': apiKey
        },
        body: JSON.stringify(data),
    };
    const response = await fetchWithFallback('/globe/types', options);
    return response.json();
}

export async function addGlobeGroup({ data, apiKey }: { data: any; apiKey: string }): Promise<any> {
    const options: RequestInit = {
        method: 'POST',
        headers: {
            'Content-Type': 'application/json',
            'API-Key': apiKey
        },
        body: JSON.stringify(data),
    };
    const response = await fetchWithFallback('/globe/groups', options);
    return response.json();
}

export async function uploadFile({ fileData, resourceData, apiKey }: { fileData: File; resourceData: any; apiKey: string }): Promise<any> {
    const formData = new FormData();
    formData.append('file', fileData);

    for (const key in resourceData) {
        if (resourceData.hasOwnProperty(key)) {
            formData.append(key, resourceData[key]);
        }
    }

    const options = {
        method: 'POST',
        headers: {
            'API-Key': apiKey
        },
        body: formData
    };

    const response = await fetchWithFallback('/resources/upload_file', options);
    clearPublicCmsCache('resources:list');
    return response.json();
}

export async function addResource({ resourceData, apiKey }: { resourceData: ResourceAdd; apiKey: string }): Promise<any> {
    const options: RequestInit = {
        method: 'POST',
        headers: {
            'Content-Type': 'application/json',
            'API-Key': apiKey
        },
        body: JSON.stringify(resourceData),
    };
    const response = await fetchWithFallback('/resources/', options);
    clearPublicCmsCache('resources:list');
    return response.json();
}

export async function loginUser(username: string, password: string): Promise<any> {
    const options: RequestInit = {
        method: 'POST',
        headers: {
            'Content-Type': 'application/json'
        },
        body: JSON.stringify({ username, password }),
    };
    const response = await fetchWithFallback('/auth/login', options);
    return response.json();
}

export async function registerUser(username: string, password: string, adminPassword: string, role?: string): Promise<any> {
    const options: RequestInit = {
        method: 'POST',
        headers: {
            'Content-Type': 'application/json',
            'Access-Key': adminPassword
        },
        body: JSON.stringify({ username, password, role }),
    };
    const response = await fetchWithFallback('/auth/register', options);
    return response.json();
}

export async function getRegistrationStatus(): Promise<{ locked: boolean }> {
    const response = await fetchWithFallback('/auth/registration/status', { method: 'GET' });
    return response.json();
}

export async function updateRegistrationStatus(locked: boolean): Promise<{ locked: boolean; message?: string }> {
    const options: RequestInit = {
        method: 'PUT',
        headers: {
            'Content-Type': 'application/json'
        },
        body: JSON.stringify({ locked }),
    };
    const response = await fetchWithFallback('/auth/registration/status', options);
    return response.json();
}

export async function addGlobeCluster({ data, apiKey }: { data: any; apiKey: string }): Promise<any> {
    const options: RequestInit = {
        method: 'POST',
        headers: {
            'Content-Type': 'application/json',
            'API-Key': apiKey
        },
        body: JSON.stringify(data),
    };
    const response = await fetchWithFallback('/globe/clusters', options);
    return response.json();
}


/***********************************************************************************************
                                        READ
***********************************************************************************************/

export async function getGlobePoints(): Promise<any> {
    return getCachedPublicJson('/globe/points', 'globe:points');
}

export async function getGlobeGroups(): Promise<any> {
    return getCachedPublicJson('/globe/groups', 'globe:groups');
}

export async function getGlobeTypes(): Promise<any> {
    return getCachedPublicJson('/globe/types', 'globe:types');
}

export async function getResources(): Promise<any> {
    return getCachedPublicJson('/resources/', 'resources:list');
}

export function preloadResources(): void {
    getResources().catch(() => undefined);
}

export async function getLandingPageContent(): Promise<{ data: LandingPageCms }> {
    return getCachedPublicJson('/cms/landing-page');
}

export async function getSiteGlobals(): Promise<any> {
    return getCachedPublicJson('/cms/site-globals');
}

export async function getTrackingSettings(): Promise<{ data: TrackingSettings }> {
    const response = await fetchWithFallback('/cms/tracking', {
        method: 'GET',
        cache: 'no-store',
    });
    return response.json();
}

export async function getCmsDocuments(): Promise<{ data: CmsDocumentSummary[] }> {
    const response = await fetchWithFallback('/cms/documents', { method: 'GET' });
    return response.json();
}

export async function getCmsDocument(contentKey: string): Promise<{ data: CmsDocumentDetail }> {
    const cached = cmsDocumentRequestCache.get(contentKey);
    if (cached && cached.expiresAt > Date.now()) return cached.request;

    const request = fetchWithFallback(
        `/cms/documents/${encodeURIComponent(contentKey)}?view=editor`,
        { method: 'GET' },
    )
        .then((response) => response.json())
        .catch((error) => {
            cmsDocumentRequestCache.delete(contentKey);
            throw error;
        });
    cmsDocumentRequestCache.set(contentKey, {
        expiresAt: Date.now() + CMS_DOCUMENT_CACHE_TTL_MS,
        request,
    });
    return request;
}

export function preloadCmsDocument(contentKey: string): void {
    getCmsDocument(contentKey).catch(() => undefined);
}

export async function createCmsDocument(data: { slug: string; title: string; content?: any }): Promise<{ data: CmsDocumentDetail }> {
    const options: RequestInit = {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(data),
    };
    const response = await fetchWithFallback('/cms/documents', options);
    clearPublicCmsCache();
    clearCmsDocumentCache();
    return response.json();
}

export async function saveCmsDocumentDraft(contentKey: string, data: {
    contentType?: string;
    title?: string;
    slug?: string;
    routePath?: string;
    seoTitle?: string;
    seoDescription?: string;
    content: any;
}): Promise<{ data: CmsDocumentDetail }> {
    const options: RequestInit = {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(data),
    };
    const response = await fetchWithFallback(`/cms/documents/${encodeURIComponent(contentKey)}/draft`, options);
    clearPublicCmsCache();
    clearCmsDocumentCache(contentKey);
    return response.json();
}

export async function deployCmsDocument(contentKey: string, target: 'staging' | 'production'): Promise<any> {
    const response = await fetchWithFallback(`/cms/documents/${encodeURIComponent(contentKey)}/deploy/${target}`, { method: 'POST' });
    clearPublicCmsCache();
    clearCmsDocumentCache(contentKey);
    return response.json();
}

export async function getCmsApprovalRequests(status: 'pending' | 'all' = 'pending'): Promise<{ data: CmsApprovalRequest[] }> {
    const response = await fetchWithFallback(`/cms/approval-requests?status=${encodeURIComponent(status)}`, { method: 'GET' });
    return response.json();
}

export async function approveCmsApprovalRequest(id: number): Promise<{ data: CmsApprovalRequest }> {
    const response = await fetchWithFallback(`/cms/approval-requests/${id}/approve`, { method: 'POST' });
    clearPublicCmsCache();
    clearCmsDocumentCache();
    return response.json();
}

export async function rejectCmsApprovalRequest(id: number, notes?: string): Promise<{ data: CmsApprovalRequest }> {
    const options: RequestInit = {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ notes }),
    };
    const response = await fetchWithFallback(`/cms/approval-requests/${id}/reject`, options);
    return response.json();
}

export async function getBannerContent(): Promise<{ data: BannerConfig }> {
    return getCachedPublicJson('/cms/banner');
}

export async function getContactSettings(): Promise<{ data: ContactSettings }> {
    const response = await fetchWithFallback('/cms/contact/settings', { method: 'GET' });
    return response.json();
}

export async function getContactSubmissions(): Promise<{ data: ContactSubmission[] }> {
    const response = await fetchWithFallback('/cms/contact/submissions', { method: 'GET' });
    return response.json();
}

export async function getCurrentUser(): Promise<any> {
    const response = await fetchWithFallback('/auth/whoami', { method: 'GET' });
    if (!response.ok) {
        throw new Error('Not logged in');
    }
    return response.json();
}

export async function logoutUser(): Promise<any> {
    const options: RequestInit = { method: 'GET', };
    const response = await fetchWithFallback('/auth/logout', options);
    return response.json();
}

export async function getUsers(): Promise<any> {
    const response = await fetchWithFallback('/auth/users', { method: 'GET' });
    return response.json();
}

export async function createUser(username: string, password: string, role?: string, requireChange = true): Promise<any> {
    const options: RequestInit = {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ username, password, role, requireChange })
    };
    const response = await fetchWithFallback('/auth/users', options);
    return response.json();
}

export async function deleteUser(userId: number): Promise<any> {
    const response = await fetchWithFallback(`/auth/users/${userId}`, { method: 'DELETE' });
    return response.json();
}

export async function updateUserRole(userId: number, role: string): Promise<any> {
    const options: RequestInit = {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ role })
    };
    const response = await fetchWithFallback(`/auth/users/${userId}/role`, options);
    return response.json();
}

export type PasswordChangePayload = {
    currentPassword: string;
    newPassword: string;
    confirmPassword: string;
};

export type AdminPasswordResetPayload = PasswordChangePayload & {
    reason: string;
    requireChange: boolean;
};

export async function resetUserPassword(userId: number, payload: AdminPasswordResetPayload): Promise<any> {
    const options: RequestInit = {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
    };
    const response = await fetchWithFallback(`/auth/users/${userId}/password`, options);
    return response.json();
}

export async function changeOwnPassword(payload: PasswordChangePayload): Promise<any> {
    const options: RequestInit = {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
    };
    const response = await fetchWithFallback('/auth/me/password', options);
    return response.json();
}

export async function getPages(): Promise<any> {
    const response = await fetchWithFallback('/cms/pages', { method: 'GET' });
    return response.json();
}

export async function getPage(slug: string): Promise<any> {
    return getCachedPublicJson(`/cms/pages/${encodeURIComponent(slug)}`, `page:${slug}`);
}

export function preloadPage(slug: string): void {
    getPage(slug).catch(() => undefined);
}

export async function savePage(slug: string, data: any): Promise<any> {
    const options: RequestInit = {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(data),
    };
    const response = await fetchWithFallback(`/cms/pages/${encodeURIComponent(slug)}`, options);
    clearPublicCmsCache();
    return response.json();
}

export async function getResourceTemplatesApi(): Promise<any> {
    return getCachedPublicJson('/cms/resources/templates');
}

export async function saveResourceTemplate(slug: string, data: any): Promise<any> {
    const options: RequestInit = {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(data),
    };
    const response = await fetchWithFallback(`/cms/resources/templates/${slug}`, options);
    clearPublicCmsCache('/cms/resources/templates');
    return response.json();
}

export async function getMediaLibrary(): Promise<any> {
    const response = await fetchWithFallback('/cms/media', { method: 'GET' });
    return response.json();
}

export async function addMediaItem(data: { url: string; alt?: string; label?: string }): Promise<any> {
    const options: RequestInit = {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(data),
    };
    const response = await fetchWithFallback('/cms/media', options);
    return response.json();
}

export async function uploadMediaItem(file: File, alt?: string, label?: string): Promise<any> {
    const form = new FormData();
    form.append('file', file);
    if (alt) form.append('alt', alt);
    if (label) form.append('label', label);
    const options: RequestInit = {
        method: 'POST',
        body: form,
    };
    const response = await fetchWithFallback('/cms/media/upload', options);
    return response.json();
}

export async function getDeployDiff(target: 'production' | 'staging' = 'production'): Promise<any> {
    const path = target === 'production' ? '/cms/deploy/diff' : '/cms/deploy/staging/diff';
    const response = await fetchWithFallback(path, { method: 'GET' });
    return response.json();
}

export async function listDeployBackups(target: 'production' | 'staging' = 'production'): Promise<any> {
    const path = target === 'production' ? '/cms/deploy/backups' : '/cms/deploy/staging/backups';
    const response = await fetchWithFallback(path, { method: 'GET' });
    return response.json();
}

export async function deployContentJson(target: 'production' | 'staging' = 'production'): Promise<any> {
    const path = target === 'production' ? '/cms/deploy' : '/cms/deploy/staging';
    const response = await fetchWithFallback(path, { method: 'POST' });
    return response.json();
}

export async function rollbackContentJson(backup: string, target: 'production' | 'staging' = 'production'): Promise<any> {
    const path = target === 'production' ? '/cms/deploy/rollback' : '/cms/deploy/staging/rollback';
    const options: RequestInit = {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ backup }),
    };
    const response = await fetchWithFallback(path, options);
    return response.json();
}

export async function getGitDiff(fromBranch = 'staging', toBranch = 'main'): Promise<any> {
    const response = await fetchWithFallback(`/cms/git/diff?from=${encodeURIComponent(fromBranch)}&to=${encodeURIComponent(toBranch)}`, { method: 'GET' });
    return response.json();
}

export async function mergeGit(fromBranch = 'staging', toBranch = 'main'): Promise<any> {
    const options: RequestInit = {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ from: fromBranch, to: toBranch }),
    };
    const response = await fetchWithFallback('/cms/git/merge', options);
    return response.json();
}

export async function getCmsConstants(): Promise<any> {
    return getCachedPublicJson('/cms/constants');
}

export async function saveCmsConstants(data: any): Promise<any> {
    const options: RequestInit = {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(data),
    };
    const response = await fetchWithFallback('/cms/constants', options);
    clearPublicCmsCache('/cms/constants');
    return response.json();
}

export async function getGlobeClusters(): Promise<any> {
    return getCachedPublicJson('/globe/clusters', 'globe:clusters');
}

export function preloadGlobeData(): void {
    Promise.all([
        getGlobeClusters(),
        getGlobeGroups(),
        getGlobeTypes(),
        getGlobePoints(),
    ]).catch(() => undefined);
}

export async function getClusterStats(clusterId: number): Promise<ClusterStat> {
    const response = await fetchWithFallback(`/globe/clusters/${clusterId}`, { method: 'GET' });
    return response.json();
}

export async function updateLandingPageContent({ data }: { data: LandingPageCms }): Promise<{ data: LandingPageCms }> {
    const options: RequestInit = {
        method: 'PUT',
        headers: {
            'Content-Type': 'application/json'
        },
        body: JSON.stringify(data),
    };
    const response = await fetchWithFallback('/cms/landing-page', options);
    clearPublicCmsCache('/cms/landing-page');
    clearPublicCmsCache('page:home');
    return response.json();
}

export async function updateBannerContent({ data }: { data: BannerConfig }): Promise<{ data: BannerConfig }> {
    const options: RequestInit = {
        method: 'PUT',
        headers: {
            'Content-Type': 'application/json'
        },
        body: JSON.stringify(data),
    };
    const response = await fetchWithFallback('/cms/banner', options);
    clearPublicCmsCache('/cms/banner');
    clearPublicCmsCache('/cms/site-globals');
    return response.json();
}

export async function saveContactSettingsApi(data: ContactSettings): Promise<{ data: ContactSettings }> {
    const options: RequestInit = {
        method: 'PUT',
        headers: {
            'Content-Type': 'application/json'
        },
        body: JSON.stringify(data),
    };
    const response = await fetchWithFallback('/cms/contact/settings', options);
    return response.json();
}

export async function submitContactMessage(payload: ContactSubmissionPayload): Promise<{ data: ContactSubmission; emailSent?: boolean }> {
    const options: RequestInit = {
        method: 'POST',
        headers: {
            'Content-Type': 'application/json'
        },
        body: JSON.stringify(payload),
    };
    const response = await fetchWithFallback('/cms/contact/submissions', options);
    return response.json();
}

export async function getActivityLogs(limit = 200): Promise<{ data: ActivityLogEntry[]; count: number }> {
    const response = await fetchWithFallback(`/cms/activity/logs?limit=${limit}`, { method: 'GET' });
    return response.json();
}

/***********************************************************************************************
                                        UPDATE
***********************************************************************************************/

export async function updateGlobePoint({ data, apiKey }: { data: any; apiKey: string }): Promise<any> {
    const options: RequestInit = {
        method: 'PUT',
        headers: {
            'Content-Type': 'application/json',
            'API-Key': apiKey
        },
        body: JSON.stringify(data),
    };
    const response = await fetchWithFallback('/globe/points', options);
    return response.json();
}

export async function updateGlobeGroup({ data, apiKey }: { data: any; apiKey: string }): Promise<any> {
    const options: RequestInit = {
        method: 'PUT',
        headers: {
            'Content-Type': 'application/json',
            'API-Key': apiKey
        },
        body: JSON.stringify(data),
    };
    const response = await fetchWithFallback('/globe/groups', options);
    return response.json();
}

export async function updateGlobeType({ data, apiKey }: { data: any; apiKey: string }): Promise<any> {
    const options: RequestInit = {
        method: 'PUT',
        headers: {
            'Content-Type': 'application/json',
            'API-Key': apiKey
        },
        body: JSON.stringify(data),
    };
    const response = await fetchWithFallback('/globe/types', options);
    return response.json();
}

export async function updateResource({ resourceData, apiKey }: { resourceData: Resource; apiKey: string }): Promise<any> {
    const options: RequestInit = {
        method: 'PUT',
        headers: {
            'Content-Type': 'application/json',
            'API-Key': apiKey
        },
        body: JSON.stringify(resourceData),
    };
    const response = await fetchWithFallback('/resources/', options);
    clearPublicCmsCache('resources:list');
    return response.json();
}

export async function updateGlobeCluster({ data, apiKey }: { data: any; apiKey: string }): Promise<any> {
    const options: RequestInit = {
        method: 'PUT',
        headers: {
            'Content-Type': 'application/json',
            'API-Key': apiKey
        },
        body: JSON.stringify(data),
    };
    const response = await fetchWithFallback(`/globe/clusters`, options);
    return response.json();
}

/***********************************************************************************************
                                        DELETE
***********************************************************************************************/

export async function deleteGlobePoints({ pointIds, apiKey }: { pointIds: number[]; apiKey: string }): Promise<any> {
    const options: RequestInit = {
        method: 'DELETE',
        headers: {
            'API-Key': apiKey
        },
    };
    const response = await fetchWithFallback(`/globe/points/${pointIds}`, options);
    return response.json();
}

export async function deleteGlobeGroup({ groupId, apiKey }: { groupId: number; apiKey: string }): Promise<any> {
    const options: RequestInit = {
        method: 'DELETE',
        headers: {
            'API-Key': apiKey
        },
    };
    const response = await fetchWithFallback(`/globe/groups/${groupId}`, options);
    return response.json();
}

export async function deleteGlobeType({ typeId, apiKey }: { typeId: number; apiKey: string }): Promise<any> {
    const options: RequestInit = {
        method: 'DELETE',
        headers: {
            'API-Key': apiKey
        },
    };
    const response = await fetchWithFallback(`/globe/types/${typeId}`, options);
    return response.json();
}

export async function deleteResources({ resourceIds, apiKey }: { resourceIds: number[]; apiKey: string }): Promise<any> {
    const options: RequestInit = {
        method: 'DELETE',
        headers: {
            'API-Key': apiKey
        },
    };

    const idsString = resourceIds.join(',');

    const response = await fetchWithFallback(`/resources/${idsString}`, options);
    clearPublicCmsCache('resources:list');
    return response.json();
}

export async function deleteGlobeCluster({ clusterIds, apiKey }: { clusterIds: number[]; apiKey: string }): Promise<any> {
    const options: RequestInit = {
        method: 'DELETE',
        headers: {
            'API-Key': apiKey
        },
    };

    const idsString = clusterIds.join(',');

    const response = await fetchWithFallback(`/globe/clusters/${idsString}`, options);
    return response.json();
}
